/**
 * MemberCard - displays a single team member
 *
 * Shows avatar placeholder (first letter), display name, type_code badge,
 * mini axis bars, and a status indicator.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import SpectrumBar from './SpectrumBar';

/* ------------------------------------------------------------------ */
/*  Status colour map                                                  */
/* ------------------------------------------------------------------ */

const STATUS_MAP = {
  active: { label: 'Active', colorKey: 'success' },
  inactive: { label: 'Inactive', colorKey: 'textMuted' },
  pending: { label: 'Pending', colorKey: 'warning' },
};

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const Card = styled.div`
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => (props.$compact ? '12px' : '20px')};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;

  &:hover {
    border-color: ${(props) =>
      props.$clickable ? props.theme.colors.primary : props.theme.colors.border};
    box-shadow: ${(props) =>
      props.$clickable ? props.theme.shadows.soft : 'none'};
    transform: ${(props) => (props.$clickable ? 'translateY(-2px)' : 'none')};
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: ${(props) => (props.$compact ? '8px' : '14px')};
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: ${(props) => (props.$compact ? '32px' : '42px')};
  height: ${(props) => (props.$compact ? '32px' : '42px')};
  border-radius: ${(props) => props.theme.borderRadius.full};
  background: ${(props) => props.theme.colors.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => (props.$compact ? '0.85rem' : '1.1rem')};
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: ${(props) => (props.$compact ? '0.85rem' : '1rem')};
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubInfo = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TypeBadge = styled(Link)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: ${(props) => props.theme.borderRadius.full};
  background: ${(props) => props.theme.colors.primaryGlow};
  border: 1px solid ${(props) => props.theme.colors.primary};
  font-family: ${(props) => props.theme.fonts.mono};
  font-size: 0.75rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
  &:hover { opacity: 0.8; }
`;

const StatusDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: ${(props) => props.theme.borderRadius.full};
  background: ${(props) => props.$color};
`;

const RoleBadge = styled.span`
  font-size: 0.7rem;
  color: ${(props) => props.theme.colors.textMuted};
  background: ${(props) => props.theme.colors.bgSecondary};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  padding: 2px 8px;
  margin-left: 6px;
`;

const AxesSection = styled.div`
  margin-top: ${(props) => (props.$compact ? '6px' : '10px')};
`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function MemberCard({ member = {}, onClick, compact = false }) {
  const theme = useTheme();

  const {
    display_name = '',
    email,
    type_code,
    struct_code,
    axes,
    status = 'active',
    role,
  } = member;

  const firstLetter = display_name ? display_name.charAt(0) : '?';
  const statusInfo = STATUS_MAP[status] || STATUS_MAP.active;
  const statusColor = theme.colors[statusInfo.colorKey] || theme.colors.textMuted;

  // Determine whether axes values need normalisation
  const needsNormalize =
    axes &&
    Object.values(axes).some((v) => typeof v === 'number' && v <= 1);

  return (
    <Card
      $compact={compact}
      $clickable={!!onClick}
      onClick={onClick}
    >
      <TopRow $compact={compact}>
        <Avatar $compact={compact}>{firstLetter}</Avatar>

        <Info>
          <Name $compact={compact}>
            {display_name}
            {role && <RoleBadge>{role}</RoleBadge>}
          </Name>
          {!compact && email && <SubInfo>{email}</SubInfo>}
          {!compact && struct_code && (
            <SubInfo style={{ fontFamily: theme.fonts.mono }}>
              {struct_code}
            </SubInfo>
          )}
        </Info>

        <StatusDot $color={statusColor} title={statusInfo.label} />

        {type_code && <TypeBadge to={`/types/${type_code}`} onClick={e => e.stopPropagation()}>{type_code}</TypeBadge>}
      </TopRow>

      {axes && (
        <AxesSection $compact={compact}>
          <SpectrumBar
            axes={axes}
            normalize={!!needsNormalize}
            compact={compact}
          />
        </AxesSection>
      )}
    </Card>
  );
}

export default MemberCard;
